import { PlayButton } from '@southfields-digital/mpxlive-components';
import { useEffect } from 'react';

import { MainClock } from 'src/types/matchControl/clock';
import { Control } from 'src/types/matchControl/control';

import { ClockStart, ExtraIn, TimeIn } from '../../components';
import TimeAdjustButtons from '../../components/TimeAdjustButtons';
import { useTimer } from '../../hooks';
import styles from '../../MatchControl.module.scss';

import CompactClockButtons from './CompactClockButtons';

const MAIN_CLOCK_ID = 'clock';
const EXTRA_TIME_ID = 'extraTime';

type ClockRowProps = {
  mainClock: MainClock;
  matchControlGraphics: Control[];
  isCompact: boolean;
  handleStartTime: (clock: MainClock) => void;
  handleStopTime: (clock: MainClock) => void;
  handleDispatchControl: (control: Control | undefined) => void;
  handleChangeTime: (clock: MainClock, time: number) => void;
  handleChangeExtraTime: (clock: MainClock, amount: number) => void;
  onAdd: (amount: number) => void;
  onSubtract: (amount: number) => void;
};

const ClockRow = ({
  mainClock,
  matchControlGraphics,
  isCompact,
  handleStartTime,
  handleStopTime,
  handleDispatchControl,
  handleChangeTime,
  handleChangeExtraTime,
  onAdd,
  onSubtract,
}: ClockRowProps) => {
  const { time: mainClockTime, setTimer } = useTimer(
    10,
    mainClock.clockTime,
    mainClock.clockCountDirection
  );

  // Update the timer with server-time offset whenever the clock state changes
  useEffect(() => {
    if (!mainClock) return;

    const clock = mainClock;
    const serverTimeOffset = clock.serverTimeUpdate
      ? new Date(clock.serverTimeUpdate).getTime() - Date.now()
      : 0;

    const startedAt = clock.clockTimeUpdatedAt
      ? new Date(clock.clockTimeUpdatedAt).getTime() - serverTimeOffset
      : null;

    setTimer(clock.clockTime || 0, startedAt ? new Date(startedAt) : null);
  }, [mainClock, mainClock?.clockTimeUpdatedAt, mainClock?.clockTime]);

  const compactClockControls = (
    <CompactClockButtons onAdd={onAdd} onSubtract={onSubtract} mainClockTime={mainClockTime ?? 0} />
  );

  const clockControls = (
    <div className={styles.ExtraInWrapper}>
      <ClockStart
        isRunning={Boolean(mainClock.clockTimeUpdatedAt)}
        handleStartTimer={() => handleStartTime(mainClock)}
        handleStopTimer={() => handleStopTime(mainClock)}
      />
      <PlayButton
        isRunning={Boolean(
          matchControlGraphics?.find(
            (control) => control.controlId === MAIN_CLOCK_ID && control.live
          )
        )}
        onClick={() =>
          handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === MAIN_CLOCK_ID))
        }
        className="!h-9 w-full place-content-center !text-lg"
      >
        CLOCK
      </PlayButton>
    </div>
  );

  const timeIn = (
    <TimeIn
      time={mainClockTime ?? 0}
      extraTime={mainClock.extraTime}
      precision="minutes"
      handleChangeTime={(time) => handleChangeTime(mainClock, mainClock.clockTime + time)}
    />
  );

  const timeAdjustButtons = (
    <TimeAdjustButtons
      onAdd={(amount) => handleChangeTime(mainClock, mainClock.clockTime + amount * 1000)}
      onSubtract={(amount) => handleChangeTime(mainClock, mainClock.clockTime - amount * 1000)}
    />
  );

  const extraIn = (
    <ExtraIn
      isRunning={matchControlGraphics?.some(
        (control) => control.controlId === EXTRA_TIME_ID && control.live
      )}
      handleChangeExtraTime={(amount) =>
        handleChangeExtraTime(mainClock, (mainClock.extraTime ?? 0) + amount)
      }
      handleToggleIsExtraTimeLive={() =>
        handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === EXTRA_TIME_ID))
      }
      extraTime={mainClock.extraTime ?? 0}
    />
  );

  if (isCompact) {
    return (
      <>
        {timeIn}
        {compactClockControls}
        <div className="flex gap-3 justify-between">
          <div className="flex gap-3">{clockControls}</div>

          {extraIn}
        </div>
      </>
    );
  }

  return (
    <>
      {clockControls}
      <div className="flex flex-col gap-3">
        {timeIn}
        {timeAdjustButtons}
      </div>
      {extraIn}
    </>
  );
};

export default ClockRow;
